import { useState, useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useDashboardInsights } from '@/hooks/useDashboardInsights';
import { OnboardingTour } from '@/components/OnboardingTour';
import { OnboardingStatusCard } from '@/components/OnboardingStatusCard';
import { TimePeriodFilter, type TimePeriodValue } from '@/components/TimePeriodFilter';
import { getDateRangeFromPreset } from '@/lib/timePeriodUtils';
// Direct imports for tree-shaking (bundle-barrel-imports)
import { NeedsAttentionHeader } from './NeedsAttentionHeader';
import { PriorityDecisionCard } from './PriorityDecisionCard';
import { CashPositionCard } from './CashPositionCard';
import { MonthlyExpensesCard } from './MonthlyExpensesCard';
import { TaxLiabilitiesCard } from './TaxLiabilitiesCard';
import { PerformanceSnapshotCard } from './PerformanceSnapshotCard';
import { SpendingBreakdownCard } from './SpendingBreakdownCard';
import { SalesChannelCard } from './SalesChannelCard';
import { ShiftPerformanceCard } from './ShiftPerformanceCard';
import { ActionNeededModule } from './ActionNeededModule';
import { useAllOnboardingComplete } from '@/hooks/useAllOnboardingComplete';

const DASHBOARD_DEFAULT_PRESET = 'this_month' as const;

export const Dashboard = () => {
  const defaultRange = useMemo(() => {
    const range = getDateRangeFromPreset(DASHBOARD_DEFAULT_PRESET);
    return {
      preset: DASHBOARD_DEFAULT_PRESET,
      startDate: range.start,
      endDate: range.end,
    } as TimePeriodValue;
  }, []);

  const [timePeriod, setTimePeriod] = useState<TimePeriodValue>(defaultRange);
  const [salesBasis, setSalesBasis] = useState<'gross' | 'net'>('net');
  const [platformFilter, setPlatformFilter] = useState<string>('all');

  const { data: insights, isLoading, error } = useDashboardInsights({
    startDate: timePeriod.startDate,
    endDate: timePeriod.endDate,
  });
  const { phase1Complete } = useAllOnboardingComplete();

  const handleTimePeriodChange = (value: TimePeriodValue) => {
    setTimePeriod(value);
  };

  const availablePlatforms = useMemo(() => {
    if (!insights?.platformSales) return [];
    return insights.platformSales
      .filter(p => p.netSales > 0 || p.grossSales > 0)
      .map(p => p.platform);
  }, [insights?.platformSales]);

  const filteredPlatformSales = useMemo(() => {
    if (!insights?.platformSales) return [];
    if (platformFilter === 'all') return insights.platformSales;
    return insights.platformSales.filter(p => p.platform === platformFilter);
  }, [insights?.platformSales, platformFilter]);

  if (isLoading) {
    const isToday = timePeriod.preset === 'today';
    return (
      <div className="space-y-8 p-6 bg-background min-h-[calc(100vh-8rem)]">
        {/* Header skeleton (only for "Today") */}
        {isToday && (
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-10 w-96" />
            <Skeleton className="h-5 w-80" />
          </div>
        )}

        {/* Priority Decisions skeleton (only for "Today") */}
        {isToday && (
          <div>
            <Skeleton className="h-5 w-40 mb-4" />
            <div className="grid gap-4 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-5">
                    <div className="space-y-3">
                      <Skeleton className="h-5 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Cash Position + Expenses Snapshot skeleton */}
        <div className={`grid gap-5 ${isToday ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
          {isToday && (
            <Card>
              <CardContent className="p-6">
                <Skeleton className="h-5 w-32 mb-4" />
                <Skeleton className="h-12 w-40 mb-4" />
                <Skeleton className="h-10 w-36 mb-4" />
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          )}
          <div className="space-y-5">
            <Card>
              <CardContent className="p-6">
                <Skeleton className="h-5 w-36 mb-4" />
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full mb-2" />
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Performance Row skeleton */}
        <div className="grid gap-6 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-5 w-40 mb-4" />
                <div className="space-y-3">
                  <Skeleton className="h-10 w-32" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Shifts skeleton */}
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-5 w-32 mb-6" />
            <div className="grid grid-cols-2 gap-6">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-destructive">Error loading dashboard: {error.message}</div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">No data available</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6 bg-background min-h-[calc(100vh-8rem)]">
      <OnboardingTour />
      <OnboardingStatusCard />
      {phase1Complete && <ActionNeededModule />}

      {/* Filters Bar */}
      <div className="flex items-center gap-4 flex-wrap">
        <TimePeriodFilter
          value={timePeriod}
          onChange={handleTimePeriodChange}
          defaultPreset={DASHBOARD_DEFAULT_PRESET}
        />

        <Separator className="h-8 w-px bg-border" orientation="vertical" />

        {/* Platform Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs text-muted-foreground font-normal whitespace-nowrap">Platform</Label>
          <div className="flex gap-1.5 flex-wrap">
            <Button
              variant={platformFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setPlatformFilter('all')}
              className={cn(
                "h-7 rounded-full px-3 text-xs font-normal",
                platformFilter === 'all' && "bg-primary text-primary-foreground"
              )}
            >
              All
            </Button>
            {availablePlatforms.map(p => (
              <Button
                key={p}
                variant={platformFilter === p ? 'default' : 'outline'}
                onClick={() => setPlatformFilter(p)}
                className={cn(
                  "h-7 rounded-full px-3 text-xs font-normal",
                  platformFilter === p && "bg-primary text-primary-foreground"
                )}
              >
                {p}
              </Button>
            ))}
          </div>
        </div>

        <Separator className="h-8 w-px bg-border" orientation="vertical" />

        {/* Sales Basis Toggle */}
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground font-normal whitespace-nowrap">
            {salesBasis === 'gross' ? 'Gross (incl. tax)' : 'Net (excl. tax)'}
          </Label>
          <Switch
            checked={salesBasis === 'net'}
            onCheckedChange={(checked) => setSalesBasis(checked ? 'net' : 'gross')}
          />
        </div>
      </div>

      {/* Header - Needs Attention (real-time, only visible for "Today") */}
      {timePeriod.preset === 'today' && (
        <NeedsAttentionHeader
          status={insights.overallStatus}
          headline={insights.headline}
          subheadline={insights.subheadline}
        />
      )}

      {/* Priority Decisions Section (real-time, only visible for "Today") */}
      {timePeriod.preset === 'today' && (
        <div className="space-y-4">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Priority Decisions
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            {insights.priorityDecisions.map((decision) => (
              <PriorityDecisionCard key={decision.id} decision={decision} />
            ))}
          </div>
        </div>
      )}

      {/* Cash Position + Expenses Snapshot + Tax Liabilities Row */}
      <div className={cn(
        "grid gap-5",
        timePeriod.preset === 'today' ? "lg:grid-cols-2" : "lg:grid-cols-1"
      )}>
        {/* Cash Position (real-time, only visible for "Today") */}
        {timePeriod.preset === 'today' && (
          <CashPositionCard
            cashPosition={insights.cashPosition}
            insight="After tax reserves and this week's share of fixed expenses."
          />
        )}
        <div className="space-y-5">
          <MonthlyExpensesCard expenses={insights.monthlyExpenses} preset={timePeriod.preset} />
          <TaxLiabilitiesCard taxLiabilities={insights.taxLiabilities} />
        </div>
      </div>

      {/* Performance Row - 3 Cards */}
      <div className="grid gap-6 md:grid-cols-3">
        <PerformanceSnapshotCard data={insights.performanceSnapshot} macroKPIs={insights.macroKPIs} salesBasis={salesBasis} />
        <SpendingBreakdownCard
          data={insights.spendingBreakdown}
          insight="Labor is the primary source of margin pressure this week."
        />
        <SalesChannelCard platforms={filteredPlatformSales} />
      </div>

      {/* Shift Performance */}
      <ShiftPerformanceCard
        topShifts={insights.topShifts}
        bottomShifts={insights.bottomShifts}
      />
    </div>
  );
};
